// @flow

import { combineEpics, ActionsObservable } from 'redux-observable';
import { of, concat, pipe, EMPTY } from 'rxjs';
import { filter, switchMap, map, distinct, takeUntil } from 'rxjs/operators';

import config from '../../config/app.config.js';
import { results } from '../../MockUp';
import { API } from '../../utils/APIUtils';
import { generateId } from '../../utils/CommonUtils';
import {
  WS_INIT_DEPOSIT,
  WS_INIT_DEPOSIT_SUCCESS,
  WS_CHECK_DEPOSIT_REQUEST,
  WS_CHECK_DEPOSIT_SUCCESS,
  WS_CHECK_DEPOSIT_ERROR,
  WS_LIST_AVAILABLE_DEPOSIT_METHODS_REQUEST,
  WS_LIST_AVAILABLE_DEPOSIT_METHODS_SUCCESS,
  WS_LIST_AVAILABLE_DEPOSIT_METHODS_ERROR,
  WS_SCAN_CREDIT_CARD_REQUEST,
  WS_SCAN_CREDIT_CARD_SUCCESS,
  WS_SCAN_CREDIT_CARD_ERROR,
  WS_START_DEPOSIT_REQUEST,
  WS_START_DEPOSIT_SUCCESS,
  WS_START_DEPOSIT_ERROR,
  WS_CLEAR_PENDING,
  WS_DISPLAY_ERROR_LAYER,
  GLOBAL_LOADING,
} from '../actions';
import type {
  RootState,
  DepositActions,
  StateObservable,
  ApiRequest,
  InitDepositParams,
  InitDepositResponse,
  InitDepositError,
  CheckDepositParams,
  CheckDepositResponse,
  CheckDepositError,
  ListAvailableDepositMethodsParams,
  ListAvailableDepositMethodsResponse,
  ListAvailableDepositMethodsError,
  ScanCreditCardParams,
  ScanCreditCardResponse,
  ScanCreditCardError,
  StartDepositParams,
  StartDepositResponse,
  StartDepositError,
} from '../types';

import { sendMessageDeposit, afterConnectDeposit } from './websocketEpics';

const afterInitDeposit = (state$: StateObservable<RootState>, codeToExecute: any) => {
  return pipe(
    switchMap((action: DepositActions) => {
      if (state$.value.deposit.initialized) {
        return codeToExecute(action);
      }

      const toPipe = [];
      if (
        !state$.value.deposit.initializing
        || (state$.value.deposit.initializing && !state$.value.deposit.initialized)
      ) {
        toPipe.push(of({ type: WS_INIT_DEPOSIT }));
      }
      toPipe.push(
        state$.pipe(
          filter(state => state.deposit.initialized),
          distinct(state => state.deposit.initialized),
          switchMap(() => {
            return codeToExecute(action);
          }),
        ),
      );

      return concat(...toPipe);
    }),
  );
};

export const initDepositEpic = (action$: ActionsObservable<DepositActions>, state$: any) =>
  action$.pipe(
    filter(action => action.type === WS_INIT_DEPOSIT),
    afterConnectDeposit(state$, (actionSend: DepositActions) => {
      if (state$.value.common.jwt) {
        const json: ApiRequest<InitDepositParams> = {
          method: 'InitDepositSession',
          params: { jwt: state$.value.common.jwt },
          id: generateId('InitDepositSession'),
        };

        return sendMessageDeposit<InitDepositParams, InitDepositResponse, InitDepositError>(json).pipe(
          map((payload) => {
            if (config.isMockUp) payload = results.InitDepositSession;
            let answer = {
              type: WS_INIT_DEPOSIT_SUCCESS,
              payload,
            };

            if (!payload.result || !payload.result.success || payload.error) {
              answer = {
                type: WS_DISPLAY_ERROR_LAYER,
                payload: {
                  ...payload.error,
                  retryAction: actionSend,
                  codeMessage: 'INIT DEPOSIT',
                  message: 'Erreur d\'initialisation du dépôt.',
                },
              };
            }
            return answer;
          }),
          takeUntil(action$.ofType(WS_INIT_DEPOSIT_SUCCESS)),
        );
      }
      return EMPTY;
    }),
  );

export const checkDepositRequestEpic = (action$: ActionsObservable<DepositActions>, state$: any) =>
  action$.pipe(
    filter(action => action.type === WS_CHECK_DEPOSIT_REQUEST && !config.isMockUp),
    afterInitDeposit(state$, (actionSend: DepositActions) => {
      if (actionSend.type === WS_CHECK_DEPOSIT_REQUEST) {
        return sendMessageDeposit<CheckDepositParams, CheckDepositResponse, CheckDepositError>({
          ...actionSend.payload,
          method: API.CheckDeposit,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_CHECK_DEPOSIT_ERROR,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              if (
                !payload.error.data
                || !payload.error.data.field_violations
                || payload.error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_CHECK_DEPOSIT_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const listAvailableDepositMethodsRequestEpic = (action$: ActionsObservable<DepositActions>, state$: any) =>
  action$.pipe(
    filter(action => action.type === WS_LIST_AVAILABLE_DEPOSIT_METHODS_REQUEST && !config.isMockUp),
    afterInitDeposit(state$, (actionSend: DepositActions) => {
      if (actionSend.type === WS_LIST_AVAILABLE_DEPOSIT_METHODS_REQUEST) {
        return sendMessageDeposit<
          ListAvailableDepositMethodsParams,
        ListAvailableDepositMethodsResponse,
        ListAvailableDepositMethodsError
        >({
          ...actionSend.payload,
          method: API.ListAvailableDepositMethods,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_LIST_AVAILABLE_DEPOSIT_METHODS_ERROR,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              if (
                !payload.error.data
                || !payload.error.data.field_violations
                || payload.error.data.field_violations[0].field === 'method'
              ) {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_LIST_AVAILABLE_DEPOSIT_METHODS_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
              {
                type: GLOBAL_LOADING,
                payload: false,
              },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const scanCreditCardRequestEpic = (action$: ActionsObservable<DepositActions>, state$: any) =>
  action$.pipe(
    filter(action => action.type === WS_SCAN_CREDIT_CARD_REQUEST && !config.isMockUp),
    afterInitDeposit(state$, (actionSend: DepositActions) => {
      if (actionSend.type === WS_SCAN_CREDIT_CARD_REQUEST) {
        return sendMessageDeposit<ScanCreditCardParams, ScanCreditCardResponse, ScanCreditCardError>({
          ...actionSend.payload,
          method: API.ScanCreditCard,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const dispatchActions = [
                {
                  type: WS_SCAN_CREDIT_CARD_ERROR,
                  payload: payload.error,
                },
                {
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: payload.error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_SCAN_CREDIT_CARD_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const startDepositRequestEpic = (action$: ActionsObservable<DepositActions>, state$: any) =>
  action$.pipe(
    filter(action => action.type === WS_START_DEPOSIT_REQUEST && !config.isMockUp),
    afterInitDeposit(state$, (actionSend: DepositActions) => {
      if (actionSend.type === WS_START_DEPOSIT_REQUEST) {
        // we add the desposit amount in the start deposit request
        const firstDeposit = state$.value.common.deposit !== null ? state$.value.common.deposit : config.depositAmount;

        actionSend.payload.params.fields.amount = firstDeposit;

        return sendMessageDeposit<StartDepositParams, StartDepositResponse, StartDepositError>({
          ...actionSend.payload,
          method: API.StartDeposit,
        }).pipe(
          switchMap((payload) => {
            if (payload.error) {
              const { error } = payload;

              // Fix for API returning fieldViolations instead of field_violations
              if (error.data && error.data.fieldViolations) {
                error.data.field_violations = error.data.fieldViolations;
              }

              const dispatchActions = [
                {
                  type: WS_START_DEPOSIT_ERROR,
                  payload: error,
                },
                { type: WS_CLEAR_PENDING },
                {
                  type: GLOBAL_LOADING,
                  payload: false,
                },
              ];

              if (!error.data || !error.data.field_violations || error.data.field_violations[0].field === 'method') {
                dispatchActions.push({
                  type: WS_DISPLAY_ERROR_LAYER,
                  payload: error,
                });
              }

              return of(...dispatchActions);
            }

            const dispatchActions = [
              {
                type: WS_START_DEPOSIT_SUCCESS,
                payload: payload.result,
              },
              { type: WS_CLEAR_PENDING },
            ];

            return of(...dispatchActions);
          }),
        );
      }
      return null;
    }),
  );

export const depositEpics = combineEpics(
  initDepositEpic,
  checkDepositRequestEpic,
  listAvailableDepositMethodsRequestEpic,
  scanCreditCardRequestEpic,
  startDepositRequestEpic,
);
